import {
  Flex,
  Heading,
  Text,
  toast,
  Dialog,
  DialogContent,
  IconButton,
  Button,
  FormInput
} from '@sparrowengg/twigs-react';
import { CloseIcon } from '@sparrowengg/twigs-react-icons';
import CloudIcon from 'commons/icons/cloud';
import DesktopIcon from 'commons/icons/desktop';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { devPortalServices } from 'commons/services';
import { defaultValues } from 'features/apps/add-local-dev-app/constants';

type AppTypeSelection = 'publish' | 'localDev' | null;

const ConfigPage = () => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [selectedAppType, setSelectedAppType] =
    useState<AppTypeSelection>(null);
  const [appNameInput, setAppNameInput] = useState('');
  const [appNameError, setAppNameError] = useState('');

  const handleCardClick = (appType: AppTypeSelection) => {
    setAppNameInput('');
    setAppNameError('');
    setSelectedAppType(appType);
  };

  const handleAppNameModalClose = () => {
    setSelectedAppType(null);
    setAppNameInput('');
    setAppNameError('');
  };

  const handleAppNameConfirm = () => {
    const trimmedName = appNameInput.trim();
    if (!trimmedName) {
      setAppNameError('App name is required');
      return;
    }

    if (selectedAppType === 'publish') {
      handleAppNameModalClose();
      navigate(`/add-apps?appName=${encodeURIComponent(trimmedName)}`);
    } else if (selectedAppType === 'localDev') {
      handleConfigureLocalDevApp(trimmedName);
    }
  };

  const handleConfigureLocalDevApp = async (appName: string) => {
    setCreating(true);
    try {
      // Prepare form data with default values
      const formData = new FormData();

      // Convert video URL if needed
      const videoUrl =
        defaultValues.videoUrl?.trim() ||
        'https://www.youtube.com/watch?v=RymrDTbydWc';
      const convertUrl = (url: string): string => {
        const youtubeRegex =
          /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(.{0,500})$/;
        if (youtubeRegex.test(url)) {
          const videoIdMatch = url.match(youtubeRegex);
          const videoId = videoIdMatch ? videoIdMatch[4] : null;
          if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;
          }
        }
        return url;
      };

      formData.append('videoUrl', convertUrl(videoUrl));
      formData.append('appName', appName);
      formData.append('appLabel', appName);
      formData.append(
        'shortDescription',
        defaultValues.shortDescription || 'new app'
      );
      formData.append(
        'appOverView',
        defaultValues.appOverView || 'local dev app'
      );
      formData.append(
        'appInstructions',
        defaultValues.appInstructions || 'new app'
      );
      formData.append(
        'supportEmail',
        defaultValues.supportEmail || 'support@gmail.com'
      );
      formData.append('supportUrl', defaultValues.supportUrl || '');
      formData.append('websiteLink', defaultValues.websiteLink || '');
      formData.append(
        'apiUrl',
        defaultValues.apiUrl || 'https://api.surveysparrow.com'
      );

      // Handle events
      if (
        Array.isArray(defaultValues.events) &&
        defaultValues.events.length > 0
      ) {
        defaultValues.events.forEach((event: string) => {
          formData.append('events[]', event);
        });
      }

      // Handle location
      if (defaultValues.location) {
        formData.append('locationName', defaultValues.location);
      }

      // Handle iParams (only if not empty)
      if (
        defaultValues.iParams &&
        typeof defaultValues.iParams === 'object' &&
        Object.keys(defaultValues.iParams).length > 0
      ) {
        formData.append('iParams', JSON.stringify(defaultValues.iParams));
      }

      // Handle oauth (only if not empty)
      if (
        defaultValues.oauth &&
        typeof defaultValues.oauth === 'object' &&
        Object.keys(defaultValues.oauth).length > 0
      ) {
        formData.append('oauth', JSON.stringify(defaultValues.oauth));
      }

      // Handle whitelistedDomains - ensure it's a simple array of trimmed strings
      if (
        Array.isArray(defaultValues.whitelistedDomains) &&
        defaultValues.whitelistedDomains.length > 0
      ) {
        // Filter out empty strings, trim each domain, and remove duplicates
        const processedDomains = defaultValues.whitelistedDomains
          .map((domain: any) => {
            // If domain is a stringified array, parse it
            if (typeof domain === 'string' && domain.trim().startsWith('[')) {
              try {
                const parsed = JSON.parse(domain);
                return Array.isArray(parsed) ? parsed : [domain];
              } catch {
                return domain.trim();
              }
            }
            return typeof domain === 'string'
              ? domain.trim()
              : String(domain).trim();
          })
          .flat() // Flatten in case we had nested arrays
          .filter((domain: string) => domain.length > 0) // Remove empty strings
          .filter(
            (domain: string, index: number, self: string[]) =>
              self.indexOf(domain) === index
          ); // Remove duplicates

        // Append each domain as a separate entry (not stringified)
        processedDomains.forEach((domain: string) => {
          formData.append('whitelistedDomains[]', domain);
        });
      }

      // Create the app
      const response = await devPortalServices.uploadLocalDevApp(formData);

      if (response?.appId) {
        // Navigate directly to edit page
        navigate(`/edit-local-dev-app/${response.appId}`);
      } else {
        toast({
          variant: 'error',
          description: 'Failed to create app. Please try again.'
        });
      }
    } catch (err) {
      console.error('Error creating local dev app:', err);
      toast({
        variant: 'error',
        description: 'Error creating app. Please try again.'
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      css={{ marginTop: 108 }}
    >
      <Flex flexDirection="column" gap="$2" alignItems="center">
        <Heading size="h5">Build or Deploy?</Heading>
        <Text size="md" css={{ color: '$neutral800' }}>
          Choose either of the two options to build your AppNest application
        </Text>
      </Flex>
      <Flex css={{ marginTop: '$20' }} gap="$12">
        <ConfigCard
          icon={<CloudIcon />}
          heading="Publish App"
          onClickHandler={() => handleCardClick('publish')}
          description="Upload the .zip file directly, fill the required information and deploy the app."
        />
        {/* <ConfigCard
          icon={<CodeIcon />}
          heading="Start from scratch"
          onClickHandler={() => navigate('/code-editor')}
          description="Quickly build the entire application from scratch within the cloud."
        /> */}
        <ConfigCard
          icon={<DesktopIcon />}
          heading="Configure Local Dev App"
          onClickHandler={() => handleCardClick('localDev')}
          description="Configure your local development app with manifest file and ngrok URL for testing."
          loading={creating}
        />
      </Flex>
      {selectedAppType && (
        <AppNameModal
          onClose={handleAppNameModalClose}
          onConfirm={handleAppNameConfirm}
          appNameInput={appNameInput}
          setAppNameInput={setAppNameInput}
          appNameError={appNameError}
          setAppNameError={setAppNameError}
          loading={creating}
        />
      )}
    </Flex>
  );
};

export default ConfigPage;

const ConfigCard = ({
  icon,
  heading,
  description,
  onClickHandler,
  loading
}: {
  icon: React.ReactNode;
  heading: string;
  description: string;
  onClickHandler: () => void;
  loading?: boolean;
}) => {
  return (
    <Flex
      onClick={loading ? undefined : onClickHandler}
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      css={{
        border: '$borderWidths$xs solid $neutral200',
        borderRadius: '$3xl',
        width: 388,
        height: 244,
        padding: '$24',
        cursor: loading ? 'wait' : 'pointer',
        transition: 'all .5s ease',
        textAlign: 'center',
        opacity: loading ? 0.6 : 1,
        '&:hover': {
          background: loading ? 'transparent' : '#F3F9FA',
          borderColor: loading ? '$neutral200' : '$primary300'
        }
      }}
    >
      {icon}
      <Flex css={{ marginTop: '$12' }} flexDirection="column" gap="$4">
        <Heading size="h6">{heading}</Heading>
        <Text size="sm" css={{ color: '$black900' }}>
          {description}
        </Text>
      </Flex>
    </Flex>
  );
};

const AppNameModal = ({
  onClose,
  onConfirm,
  appNameInput,
  setAppNameInput,
  appNameError,
  setAppNameError,
  loading
}: {
  onClose: () => void;
  onConfirm: () => void;
  appNameInput: string;
  setAppNameInput: React.Dispatch<React.SetStateAction<string>>;
  appNameError: string;
  setAppNameError: React.Dispatch<React.SetStateAction<string>>;
  loading?: boolean;
}) => {
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.FormEvent<HTMLInputElement>
  ) => {
    setAppNameInput(e.currentTarget.value);
    if (appNameError) {
      setAppNameError('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onConfirm();
    }
  };

  return (
    <Dialog open>
      <DialogContent css={{ padding: 0, borderRadius: '$xl', width: 440 }}>
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">Name Your App</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={onClose}
          />
        </Flex>
        <Flex flexDirection="column" gap="$10" css={{ padding: '$12' }}>
          <FormInput
            label="App Name"
            requiredIndicator
            size="lg"
            maxLength={100}
            placeholder="Enter your app name"
            value={appNameInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            error={appNameError}
            autoFocus
          />
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="flex-end"
          gap="$6"
          css={{ padding: '$12', paddingTop: 0 }}
        >
          <Button
            size="lg"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            color="primary"
            onClick={onConfirm}
            disabled={!appNameInput.trim() || loading}
            loading={loading}
          >
            Continue
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};
